import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  GoogleAuthProvider,
  browserPopupRedirectResolver,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from 'firebase/auth'
import { auth } from '../lib/firebase'
import { ContextoSesion } from './ContextoSesion'
import { useCierreSesionPorInactividad } from '../hooks/useCierreSesionPorInactividad'

const proveedorGoogle = new GoogleAuthProvider()

const mensajesPorCodigo = {
  'auth/popup-closed-by-user': null, // el usuario cerró el popup: no es un error que mostrar
  'auth/cancelled-popup-request': null,
  'auth/network-request-failed': 'Revisa tu conexión e intenta de nuevo.',
}

/**
 * Sesión de Google en toda la app. Un único listener de onAuthStateChanged
 * en la raíz evita que cada componente que necesita saber si hay usuario
 * (encabezado, favoritos, reseñas) abra su propia suscripción.
 */
export function SesionProvider({ children }) {
  const [usuario, setUsuario] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [sesionExpirada, setSesionExpirada] = useState(false)

  useEffect(() => onAuthStateChanged(auth, (u) => {
    setUsuario(u)
    setCargando(false)
    // Al iniciar sesión de nuevo (a mano o con otra pestaña), el aviso de la
    // vez anterior ya no aplica.
    if (u) setSesionExpirada(false)
  }), [])

  const iniciarSesion = useCallback(async () => {
    setError(null)
    try {
      // auth se creó sin popupRedirectResolver (ver firebase.js), así que el
      // iframe pesado de Firebase Auth no se toca hasta esta línea: pasar el
      // resolver aquí, explícito, es lo que dispara su inicialización — y
      // solo pasa si alguien de verdad pulsa "Iniciar sesión".
      await signInWithPopup(auth, proveedorGoogle, browserPopupRedirectResolver)
      return true
    } catch (err) {
      const mensaje = mensajesPorCodigo[err.code]
      if (mensaje !== null) {
        console.error('iniciarSesion:', err)
        setError(mensaje ?? 'No pudimos iniciar sesión con Google. Intenta de nuevo.')
      }
      return false
    }
  }, [])

  const cerrarSesion = useCallback(() => signOut(auth), [])

  const alExpirarPorInactividad = useCallback(() => {
    setSesionExpirada(true)
    signOut(auth)
  }, [])
  useCierreSesionPorInactividad(!!usuario, alExpirarPorInactividad)

  const descartarAvisoExpiracion = useCallback(() => setSesionExpirada(false), [])

  const valor = useMemo(
    () => ({
      usuario,
      cargando,
      error,
      iniciarSesion,
      cerrarSesion,
      sesionExpirada,
      descartarAvisoExpiracion,
    }),
    [usuario, cargando, error, iniciarSesion, cerrarSesion, sesionExpirada, descartarAvisoExpiracion]
  )

  return <ContextoSesion.Provider value={valor}>{children}</ContextoSesion.Provider>
}
